import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { usePages } from 'api/portal/usePagesApi';

import DataTable from 'components/DataTableComponent/DataTable';
import DialogModal from 'components/DialogModal/DialogModal';
import {
  modalConfigs,
  PORTAL_MODAL_NAMES
} from 'components/DialogModalManager/constants/modalConfigs';

import { useContentManagement } from '../../../api/portal/useContentManagement';
import EmptyState from '../../../components/EmptyState/EmptyState';
import PortalHeader from '../../../components/PortalHeader/PortalHeader';
import RichText from '../../../components/RichText/RichText';
import { EToasterStatus, showToast } from '../../../utils/showToast';

import { getContentTableColumns } from './tableConfig';

export type ContentTableDataType = {
  checkbox: null;
  title: string;
  creationDate: string;
  lastPublishedDate: string;
  tag?: string;
  state: 'visible' | 'hidden' | 'scheduled';
  actions: null;
  page?: string;
};

const ContentTable = () => {
  const { getCreatedContentsData, deletePageContent } = useContentManagement();
  const { getPages } = usePages();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState<null | string>(null);
  const createdContentsData = getCreatedContentsData?.data?.contents;
  const createdPaginationData = getCreatedContentsData?.data?.pagination;
  const isLoading = getCreatedContentsData?.isLoading;
  const pagesData = getPages?.data?.pages;

  const filteredContents = createdContentsData?.filter(
    (content: { title: string }) =>
      content.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeleteSelectedContent = useCallback(async () => {
    if (!selectedContentId) return;
    try {
      await deletePageContent.mutateAsync(selectedContentId);
      await getCreatedContentsData.refetch();
      showToast({
        message: 'Page content was successfully deleted',
        status: EToasterStatus.SUCCESS
      });
    } catch (error: any) {
      showToast({
        message:
          error?.response?.data?.message ||
          'Page content was not deleted. Please try again',
        status: EToasterStatus.ERROR
      })
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedContentId(null);
    }
  }, [deletePageContent, getCreatedContentsData, selectedContentId]);

  const showDeleteContentModal = useCallback((contentId: string) => {
    setSelectedContentId(contentId);
    setIsDeleteDialogOpen(true);
  }, []);

  const columns: ColumnDef<Partial<ContentTableDataType>>[] = useMemo(
    () =>
      getContentTableColumns(showDeleteContentModal, navigate, pagesData),
    [showDeleteContentModal, navigate, pagesData]
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <CircularProgress className="absolute top-1/2 left-1/2 h-16 w-16" />
      );
    }

    if (!createdContentsData?.length) {
      return (
        <EmptyState
          dataTestId="ContentTable-EmptyState"
          title="Create content"
          description="Write content that you can add to pages in your portal"
          buttonText="Start now"
          className="mx-auto mt-36"
          onClick={() => navigate('./form')}
        />
      );
    }

    return (
      <div className="border border-base-sidebarBorder rounded-[20px] mx-7 mb-7 p-9">
        <div data-testid="ContentTable-RichText">
          <RichText
            title="Content"
            description="Create, view, and manage content in your portal"
            className="mb-5"
          />
        </div>
        <div className="border border-base-sidebarBorder rounded-lg">
          <DataTable
            data={filteredContents}
            columns={columns}
            showPagination={
              createdPaginationData?.totalPages >
              createdPaginationData?.documentsInPage
            }
            rowIdIdentifier="id"
            rowsPerPage={createdPaginationData?.documentsInPage}
            bodyDataTestId="ContentTable-DataTable"
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    return () => {
      const body = document.body;
      if (body.style.pointerEvents === 'none') {
        body.style.pointerEvents = '';
      }
    };
  }, [isDeleteDialogOpen]);

  return (
    <div className="h-screen overflow-scroll">
      <PortalHeader
        title={createdContentsData?.length > 0 ? 'My Content' : 'Content'}
        primaryButtonText="Create New content"
        onPrimaryButtonClick={() => navigate('./form')}
        isSearchVisible={createdContentsData?.length > 0}
        isCreateButton
        onSearchChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchQuery(e.target.value)
        }
      />
      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          onOpenChange={(open: boolean) => {
            if (!open) setIsDeleteDialogOpen(false);
          }}
          title={modalConfigs[PORTAL_MODAL_NAMES.DELETE_CONTENT].title}
          description={modalConfigs[PORTAL_MODAL_NAMES.DELETE_CONTENT].description}
          modalActions={{
            onConfirm: { label: 'Delete', action: handleDeleteSelectedContent },
            onClose: {
              label: 'Cancel',
              action: () => setIsDeleteDialogOpen(false),
              variant: 'link'
            }
          }}
        />
      )}
      {renderContent()}
    </div>
  );
};

export default ContentTable;
